import React from 'react';
import {
  Box,
  Typography,
  Paper,
  ListItem,
  ListItemText,
  Button,
} from '@mui/material';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { COLORS_ADMIN_PAGE } from '../../../conf/constants';
import { Municipality } from '../../../administration/type';

interface MunicipalityDroppableListProps {
  municipalities: Municipality[];
  selectedMunicipalities: number[];
  droppableId: string;
  onSelectMunicipality: (id: number) => void;
  showHeader?: boolean;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

const MunicipalityDroppableList: React.FC<MunicipalityDroppableListProps> = ({
  municipalities,
  selectedMunicipalities,
  droppableId,
  onSelectMunicipality,
  showHeader = false,
  loading = false,
  error = null,
  onRetry
}) => {
  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 400,
        border: `1px solid ${COLORS_ADMIN_PAGE.BORDER}`,
        borderRadius: 1
      }}>
        <Typography variant="body2" sx={{ color: COLORS_ADMIN_PAGE.TEXT_SECONDARY }}>
          Loading municipalities...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: 400,
        border: `1px solid ${COLORS_ADMIN_PAGE.BORDER}`,
        borderRadius: 1,
        p: 2
      }}>
        <Typography variant="body2" sx={{ color: 'error.main', mb: 2, textAlign: 'center' }}>
          Error loading municipalities: {error}
        </Typography>
        {onRetry && (
          <Button
            variant="outlined"
            size="small"
            onClick={onRetry}
            sx={{
              borderColor: COLORS_ADMIN_PAGE.PRIMARY,
              color: COLORS_ADMIN_PAGE.PRIMARY
            }}
          >
            Retry
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Droppable droppableId={droppableId}>
      {(provided) => (
        <Paper
          {...provided.droppableProps}
          ref={provided.innerRef}
          sx={{
            height: 400,
            p: 0,
            overflowY: 'auto',
            border: `1px solid ${COLORS_ADMIN_PAGE.BORDER}`,
            borderRadius: 1,
            boxShadow: 'none'
          }}
        >
          {showHeader && (
            <Box sx={{
              bgcolor: COLORS_ADMIN_PAGE.PRIMARY,
              color: COLORS_ADMIN_PAGE.WHITE,
              p: 2,
              position: 'sticky',
              top: 0,
              zIndex: 1,
              borderRadius: '8px 8px 0 0'
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                All Municipalities
              </Typography>
            </Box>
          )}
          <Box sx={{ p: 2 }}>
            {municipalities.map((municipality, index) => (
              <Draggable key={municipality.id} draggableId={municipality.id.toString()} index={index}>
                {(provided, snapshot) => (
                  <ListItem
                    component="div"
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    onClick={() => onSelectMunicipality(municipality.id)}
                    sx={{
                      mb: 1,
                      bgcolor: selectedMunicipalities.includes(municipality.id)
                        ? COLORS_ADMIN_PAGE.PRIMARY
                        : COLORS_ADMIN_PAGE.LIGHT_GRAY,
                      color: selectedMunicipalities.includes(municipality.id)
                        ? COLORS_ADMIN_PAGE.WHITE
                        : COLORS_ADMIN_PAGE.BLACK,
                      cursor: 'pointer',
                      borderRadius: 1,
                      py: 2,
                      px: 4,
                      border: 'none',
                      opacity: snapshot.isDragging ? 0.8 : 1,
                      transform: snapshot.isDragging ? 'rotate(5deg)' : 'none',
                      '&:hover': {
                        bgcolor: selectedMunicipalities.includes(municipality.id)
                          ? COLORS_ADMIN_PAGE.PRIMARY
                          : COLORS_ADMIN_PAGE.BACKGROUND,
                      },
                    }}
                  >
                    <ListItemText
                      primary={municipality.name + ' - ' + municipality.province.name}
                      primaryTypographyProps={{
                        fontSize: '14px',
                        fontWeight: 400
                      }}
                    />
                  </ListItem>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </Box>
        </Paper>
      )}
    </Droppable>
  );
};

export default MunicipalityDroppableList;